"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "./dialog.tsx";
import { opaqueBounds, scaleBounds, type Bounds } from "./crop.ts";
import { ImageIcon, UploadIcon } from "./icons.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Uploading and cropping.

   The unstyled `<input type="file">` is still in the DOM, still focusable and
   still the thing that opens the picker; it is laid over the drop zone at zero
   opacity. Replacing it with a click handler on a div is how a file field
   stops working for a keyboard, and it is a very common way to do it.

   The crop is done in a canvas here rather than on the server, because the
   operator has to see the result before it is committed and a round trip per
   nudge is not that.
   ────────────────────────────────────────────────────────────────────────── */

export type CropResult = {
  blob: Blob;
  width: number;
  height: number;
  /** object-position for the rendered page, when the crop is not applied. */
  focal: string;
};

const MAX_BYTES = 12 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/avif,image/gif";

export function Dropzone({
  onFile,
  label = "Drop an image, or choose one",
  hint = "PNG, JPEG, WebP or AVIF. Up to 12MB.",
  disabled = false,
}: {
  onFile: (file: File) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accept = useCallback(
    (file: File | undefined | null) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        setError("That is not an image.");
        return;
      }
      if (file.size > MAX_BYTES) {
        setError(`That file is ${(file.size / 1048576).toFixed(1)}MB. The limit is 12MB.`);
        return;
      }
      setError(null);
      onFile(file);
    },
    [onFile]
  );

  return (
    <div className="col" style={{ gap: 6 }}>
      <div
        className={`dropzone${over ? " over" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          accept(event.dataTransfer.files?.[0]);
        }}
      >
        <span style={{ color: "var(--faint)" }}>
          <UploadIcon size={19} />
        </span>
        <strong>{label}</strong>
        <small>{hint}</small>
        <input
          type="file"
          accept={ACCEPT}
          disabled={disabled}
          aria-label={label}
          onChange={(event) => {
            accept(event.target.files?.[0]);
            // Cleared so choosing the same file twice fires a change both times.
            event.target.value = "";
          }}
        />
      </div>
      {error ? (
        <span className="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

/** Longest edge of the thumbnail the transparency scan runs on. */
const SCAN = 320;

/**
 * The picture's box inside a loaded image, in the image's own pixels.
 *
 * Scanned on a thumbnail rather than the original: a 12 megapixel photograph
 * is 48MB of RGBA and a loop nobody should run on the main thread, and a
 * transparent margin is a coarse feature that survives being shrunk.
 *
 * Returns null when it cannot tell, which every caller reads as "the whole
 * file": a cross-origin image taints the canvas and throws on read, and a
 * fully transparent file has no picture to find.
 */
function measureContent(image: HTMLImageElement): Bounds | null {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (!width || !height) return null;
  try {
    const k = Math.min(1, SCAN / Math.max(width, height));
    const w = Math.max(1, Math.round(width * k));
    const h = Math.max(1, Math.round(height * k));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(image, 0, 0, w, h);
    const box = opaqueBounds(context.getImageData(0, 0, w, h).data, w, h);
    if (!box) return null;
    // Already the whole thing: say so plainly rather than through a rounding.
    if (box.x === 0 && box.y === 0 && box.width === w && box.height === h) {
      return { x: 0, y: 0, width, height };
    }
    return scaleBounds(box, { width: w, height: h }, { width, height });
  } catch {
    return null;
  }
}

/* ── cropper ───────────────────────────────────────────────────────────── */

export function CropDialog({
  file,
  aspect = 1,
  circle = true,
  outputSize = 1024,
  onCancel,
  onDone,
  saving = false,
  failure = null,
}: {
  file: File | null;
  aspect?: number;
  circle?: boolean;
  outputSize?: number;
  onCancel: () => void;
  onDone: (result: CropResult) => void | Promise<void>;
  /** The upload is in flight. The dialog stays put and says so. */
  saving?: boolean;
  /** It did not work, in words worth reading. */
  failure?: string | null;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const [natural, setNatural] = useState({ width: 0, height: 0 });
  /**
   * The picture's own box inside the file, which is not always the whole file.
   * Null until it has been measured, and for a photograph it comes back as the
   * whole thing. See `opaqueBounds`.
   */
  const [content, setContent] = useState<Bounds | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);
  const stage = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    if (!file) {
      setSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setSrc(url);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setContent(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // The image is scaled to cover the stage at zoom 1, so there is never a gap
  // at the edge no matter which way the source is oriented.
  //
  // Cover the stage with the *picture*, not with the file. They are the same
  // rectangle for a photograph, and for a file with a transparent margin they
  // are not: covering the file there would fill the frame with margin, which
  // leaves the crop transparent and reaches the public page as a black bar.
  const stageWidth = stage.current?.clientWidth ?? 380;
  const stageHeight = stageWidth / aspect;
  const picture = content ?? { x: 0, y: 0, width: natural.width, height: natural.height };
  const cover =
    picture.width && picture.height ? Math.max(stageWidth / picture.width, stageHeight / picture.height) : 1;
  const scale = cover * zoom;
  const drawWidth = picture.width * scale;
  const drawHeight = picture.height * scale;

  const clamp = useCallback(
    (next: { x: number; y: number }) => {
      const minX = Math.min(0, stageWidth - drawWidth);
      const minY = Math.min(0, stageHeight - drawHeight);
      return {
        x: Math.min(0, Math.max(minX, next.x)),
        y: Math.min(0, Math.max(minY, next.y)),
      };
    },
    [stageWidth, stageHeight, drawWidth, drawHeight]
  );

  useEffect(() => {
    setOffset((current) => clamp(current));
  }, [clamp]);

  const render = async () => {
    if (!src || !natural.width) return;
    setBusy(true);
    try {
      const image = new Image();
      image.src = src;
      await image.decode();
      const canvas = document.createElement("canvas");
      const outHeight = Math.round(outputSize / aspect);
      canvas.width = outputSize;
      canvas.height = outHeight;
      const context = canvas.getContext("2d");
      if (!context) throw new Error("no canvas context");
      context.imageSmoothingQuality = "high";
      // The stage is a window onto the scaled image; the same window in source
      // pixels is what gets drawn.
      const ratio = outputSize / stageWidth;
      context.drawImage(
        image,
        picture.x - offset.x / scale,
        picture.y - offset.y / scale,
        stageWidth / scale,
        stageHeight / scale,
        0,
        0,
        outputSize,
        outHeight
      );
      void ratio;
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/png", 0.98));
      if (!blob) throw new Error("could not encode the crop");
      const focalX = drawWidth > stageWidth ? ((-offset.x + stageWidth / 2) / drawWidth) * 100 : 50;
      const focalY = drawHeight > stageHeight ? ((-offset.y + stageHeight / 2) / drawHeight) * 100 : 50;
      onDone({
        blob,
        width: outputSize,
        height: outHeight,
        focal: `${focalX.toFixed(1)}% ${focalY.toFixed(1)}%`,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(file)}
      onClose={onCancel}
      title="Position the image"
      description="Drag to move, scroll or use the slider to zoom. What is inside the frame is what gets saved."
      footer={
        <>
          <button type="button" className="secondary-button" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="button brass"
            onClick={render}
            disabled={busy || saving || !natural.width}
          >
            {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : null}
            {busy ? "Preparing\u2026" : saving ? "Saving\u2026" : failure ? "Try again" : "Use this crop"}
          </button>
        </>
      }
    >
      {failure ? (
        <span className="field-error shake" role="alert" style={{ marginBottom: 10 }}>
          {failure}
        </span>
      ) : null}
      <div
        className="crop-stage"
        ref={stage}
        style={{ aspectRatio: String(aspect) }}
        onPointerDown={(event) => {
          (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY, ox: offset.x, oy: offset.y };
        }}
        onPointerMove={(event) => {
          if (!drag.current || event.buttons !== 1) return;
          setOffset(
            clamp({
              x: drag.current.ox + (event.clientX - drag.current.x),
              y: drag.current.oy + (event.clientY - drag.current.y),
            })
          );
        }}
        onPointerUp={() => {
          drag.current = null;
        }}
        onWheel={(event) => {
          setZoom((value) => Math.min(4, Math.max(1, value - event.deltaY * 0.0016)));
        }}
        role="application"
        aria-label="Image crop area"
        tabIndex={0}
        onKeyDown={(event) => {
          const step = event.shiftKey ? 24 : 8;
          const map: Record<string, [number, number]> = {
            ArrowLeft: [step, 0],
            ArrowRight: [-step, 0],
            ArrowUp: [0, step],
            ArrowDown: [0, -step],
          };
          const delta = map[event.key];
          if (!delta) return;
          event.preventDefault();
          setOffset((current) => clamp({ x: current.x + delta[0], y: current.y + delta[1] }));
        }}
      >
        {src ? (
          <img
            src={src}
            alt=""
            onLoad={(event) => {
              const target = event.currentTarget;
              setNatural({ width: target.naturalWidth, height: target.naturalHeight });
              setContent(measureContent(target));
            }}
            style={{
              // The element is the whole file; the box above is the picture
              // inside it, so the element is pulled back by the margin that
              // was trimmed. With nothing trimmed both terms are zero and this
              // is the plain scaled image.
              width: natural.width * scale || undefined,
              height: natural.height * scale || undefined,
              left: offset.x - picture.x * scale,
              top: offset.y - picture.y * scale,
              maxWidth: "none",
            }}
          />
        ) : null}
        <div className={`crop-mask${circle ? " circle" : " thirds"}`} />
      </div>

      <div className="row" style={{ gap: 10 }}>
        <span className="eyebrow" style={{ flex: "none" }}>
          Zoom
        </span>
        <input
          type="range"
          min={1}
          max={4}
          step={0.01}
          value={zoom}
          onChange={(event) => setZoom(Number(event.target.value))}
          aria-label="Zoom"
          style={{ accentColor: "var(--accent)", padding: 0, minHeight: 0, border: 0, background: "transparent" }}
        />
        <span className="tabular faint" style={{ fontSize: 10, minWidth: 34, textAlign: "right" }}>
          {zoom.toFixed(2)}×
        </span>
      </div>
    </Modal>
  );
}

/* ── the picker that combines both ─────────────────────────────────────── */

export function ImageField({
  value,
  previewUrl,
  onUpload,
  onClear,
  label,
  aspect = 1,
  circle = true,
  library,
  onPickFromLibrary,
  uploading = false,
}: {
  value: string | null;
  previewUrl: string | null;
  onUpload: (blob: Blob, focal: string) => void | Promise<void>;
  onClear: () => void;
  label: string;
  aspect?: number;
  circle?: boolean;
  library?: { id: string; url: string; label?: string | null }[];
  onPickFromLibrary?: (id: string) => void;
  uploading?: boolean;
}) {
  const [pending, setPending] = useState<File | null>(null);
  /**
   * The upload, while it is happening.
   *
   * The crop box used to close the instant it was confirmed and then upload in
   * the background, which for a real photograph is several seconds of nothing
   * at all — and if it failed, the failure had nowhere to appear. So the box
   * stays until the bytes are stored, says what it is doing, and holds on to
   * the crop if it goes wrong so it can be tried again rather than redone.
   */
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  return (
    <div className="col" style={{ gap: 9 }}>
      {previewUrl ? (
        <div className="row" style={{ gap: 11 }}>
          <span
            style={{
              width: 62,
              height: 62 / aspect,
              borderRadius: circle ? "50%" : 9,
              overflow: "hidden",
              flex: "none",
              boxShadow: "inset 0 0 0 1px var(--line)",
              background: "var(--ink-2)",
            }}
          >
            <img src={previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          </span>
          <div className="col" style={{ gap: 5, minWidth: 0 }}>
            {/* The storage id used to be printed here. It is not a name, it is
                not a filename, and it means nothing to whoever uploaded the
                picture — the picture is right beside it. */}
            <div className="row tight">
              <label className="secondary-button tiny" style={{ position: "relative", overflow: "hidden" }}>
                Replace
                <input
                  type="file"
                  accept={ACCEPT}
                  aria-label={`Replace ${label}`}
                  style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    if (file) setPending(file);
                    event.target.value = "";
                  }}
                />
              </label>
              <button type="button" className="text-button tiny danger" onClick={onClear}>
                Remove
              </button>
            </div>
          </div>
          {uploading ? <span className="spinner" /> : null}
        </div>
      ) : (
        <Dropzone onFile={setPending} label={`Add ${label.toLowerCase()}`} disabled={uploading} />
      )}

      {library && library.length > 0 && onPickFromLibrary ? (
        <details>
          <summary style={{ fontSize: 9.5, color: "var(--faint)", cursor: "pointer" }}>
            Or reuse one already uploaded ({library.length})
          </summary>
          <div className="media-grid" style={{ marginTop: 8 }}>
            {library.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`media-tile${item.id === value ? " on" : ""}`}
                onClick={() => onPickFromLibrary(item.id)}
                title={item.label ?? item.id}
              >
                <img src={item.url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </details>
      ) : null}

      <CropDialog
        file={pending}
        aspect={aspect}
        circle={circle}
        saving={saving}
        failure={failure}
        onCancel={() => {
          if (saving) return;
          setPending(null);
          setFailure(null);
        }}
        onDone={async (result) => {
          setSaving(true);
          setFailure(null);
          try {
            await onUpload(result.blob, result.focal);
            setPending(null);
          } catch (error) {
            setFailure(error instanceof Error ? error.message : "The image could not be saved.");
          } finally {
            setSaving(false);
          }
        }}
      />
    </div>
  );
}
